import type { TableColumn, TableRow, TableSortOptions } from "components/shared/CollapsableTable/types";
import type { IChannel } from "containers/Channels/types";
import { formatLocaleDateString } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AnalyticsFilters, IDatesRange } from "types";

import type { StoredPreferences } from "../Dashboard/utils";
import type { TitlesPaginationData } from "./Filters";
import Filters from "./Filters";
import Tables from "./Tables";
import { fetchAnalyticsConsolidatedTotals, getStoreKeyFromFilters } from "store/analyticsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";

import { useIsAnalyticsRefreshing } from "../RefreshProgress/context";
import useChannelsSales from "./useChannelsSales";

// The server runs up to 3 analytics reads per company at once
const MAX_PARALLEL_TOTALS = 3;

export interface IConsolidatedSales {
    periodsDateRange: IDatesRange;
    titlesPaginationData: TitlesPaginationData;
    filters?: AnalyticsFilters;
    onFiltersChange?: (o: AnalyticsFilters) => void;
}

const ConsolidatedSalesContent = ({
    periodsDateRange,
    titlesPaginationData,
    filters,
    onFiltersChange,
    storedPreferences,
    channels,
}: IConsolidatedSales & { storedPreferences: StoredPreferences; channels: IChannel[] }) => {
    const { t } = useTranslation();

    const { companyInfos } = useCompanySettings();

    const [tableYPos, setTableYPos] = useState<Array<{ id: string; y: number; b: number }>>([]);
    const [windowHeight, setWindowHeight] = useState<number>(0);
    const [collapsedState, setCollapsedState] = useState<Record<string, boolean>>({});
    const [visibleTableIdsKey, setVisibleTableIdsKey] = useState<string>("");

    const columns: TableColumn[] = [
        { id: "title", label: t("Analytics.ConsolidatedSales.Titles"), align: "left" },
        { id: "qty-returned", label: t("Analytics.ConsolidatedSales.QtyReturned"), align: "right", width: 90 },
        { id: "qty-net", label: t("Analytics.ConsolidatedSales.Qty"), align: "right", width: 100 },
        { id: "ca-ppht", label: t("Analytics.ConsolidatedSales.Revenue"), align: "right", width: 100 },
        { id: "rev", label: t("Analytics.ConsolidatedSales.RevenuePostDistributor"), align: "right", width: 130 },
    ];

    const sort: TableSortOptions = useMemo(() => {
        return {
            column: filters?.sortColumn || "title",
            direction: filters?.sortDirection || "asc",
        };
    }, [filters]);

    const visibleTableIds = useRef<string[]>([]);

    const containerRef = useRef<HTMLDivElement>(null);
    const tableRefs = useRef<Record<string, HTMLDivElement>>({});

    const handleColumnClick = useCallback(
        ({ id: columnId }) => {
            const isNewColumn = columnId !== sort.column;

            onFiltersChange?.({
                ...filters,
                sortColumn: columnId,
                sortDirection: (isNewColumn ? sort?.direction : sort?.direction === "asc" ? "desc" : "asc") || "asc",
            });
        },
        [sort, filters, onFiltersChange],
    );

    const handleOptionSelect = useCallback(
        (optionKey: string, value: any) => {
            onFiltersChange?.({
                ...filters,
                [optionKey]: value,
            });
        },
        [filters, onFiltersChange],
    );

    const companyId = companyInfos?.id;
    const channelIds = useMemo(() => {
        return channels ? [...channels.map((c) => c.id), "all"] : [];
    }, [channels]);

    const { fetchNextPage, tablesData } = useChannelsSales({
        // The server runs up to 3 analytics reads per company at once, the next ones wait there
        maxParallelFetch: 3,
        ...filters,
    });

    const fetchNextPageRef = useRef<typeof fetchNextPage>();
    const isRefreshing = useIsAnalyticsRefreshing();
    const dispatch = useAppDispatch();
    const { analyticsConsolidatedTotals } = useAppSelector((state) => state.analytics);

    // Tables are collapsed by default and only show their total: totals load table by table, top first,
    // 3 at a time, and a table loads its rows once unfolded. A table whose total cannot come this way
    // (server without totals, failed request) loads its first page when visible, as before.
    const totalsKey = useMemo(
        () => getStoreKeyFromFilters({ dates: filters?.dates, titleIds: filters?.titleIds }),
        [filters?.dates, filters?.titleIds],
    );
    const isCurrentTotals = analyticsConsolidatedTotals.key === totalsKey;
    const totalsUnavailable = isCurrentTotals && analyticsConsolidatedTotals.unavailable;
    const totalsByChannel = isCurrentTotals ? analyticsConsolidatedTotals.byChannel : {};
    const totalsStatus: Record<string, TFetchStatus> = useMemo(
        () => (isCurrentTotals ? analyticsConsolidatedTotals.status : {}),
        [isCurrentTotals, analyticsConsolidatedTotals.status],
    );
    const hasTotal = (channelId: string) => !totalsUnavailable && totalsStatus[channelId] === "succeeded";
    const hasNoTotal = (channelId: string) =>
        totalsUnavailable || totalsStatus[channelId] === "rejected" || totalsStatus[channelId] === "failed";

    useEffect(() => {
        if (isRefreshing || totalsUnavailable || !filters?.dates?.endDate) return;

        const loading = channelIds.filter((channelId) => totalsStatus[`${channelId}`] === "pending").length;
        channelIds
            .filter((channelId) => !totalsStatus[`${channelId}`])
            .slice(0, Math.max(0, MAX_PARALLEL_TOTALS - loading))
            .forEach((channelId) => {
                dispatch(
                    fetchAnalyticsConsolidatedTotals({
                        key: totalsKey,
                        channelId: `${channelId}`,
                        dates: filters.dates,
                        titleIds: filters.titleIds,
                    }),
                );
            });
    }, [
        isRefreshing,
        totalsUnavailable,
        totalsKey,
        totalsStatus,
        channelIds,
        filters?.dates,
        filters?.titleIds,
        dispatch,
    ]);

    fetchNextPageRef.current = fetchNextPage;

    const getRowsOfChannel = (
        channelId: string,
        columns: TableColumn[],
    ): { inRangeRows: TableRow[]; cumulatedRows: TableRow[] } => {
        const channelData = tablesData?.[channelId];

        // A channel absent from its totals answer has no sales in the range: its total is empty, as when loaded
        if (!channelData && !hasTotal(channelId)) {
            return {
                inRangeRows: [],
                cumulatedRows: [],
            };
        }

        const tableData = channelData?.data || [];
        const rowOfTotals = channelData?.rowOfTotals ?? totalsByChannel[channelId];

        const inRangeRows: TableRow[] = tableData.map(({ inRange }) => {
            return {
                id: inRange.id,
                cells: columns.map((col) => ({
                    columnId: col.id,
                    value: inRange[col.id],
                })),
            };
        });

        inRangeRows.push({
            id: `${channelId}-in-range-total`,
            cells: columns.map((col) => ({
                columnId: col.id,
                value: col.id === "title" ? t("Analytics.ConsolidatedSales.Total") : rowOfTotals?.inRange?.[col.id],
            })),
            variant: "highlighted",
            placement: "footer",
        });

        const cumulatedRows: TableRow[] = tableData.map(({ cumulated }) => {
            return {
                id: cumulated.id,
                cells: columns.map((col) => ({
                    columnId: `cumulated-${col.id}`,
                    value: cumulated[col.id],
                })),
            };
        });

        cumulatedRows.push({
            id: `${channelId}-cumulated-total`,
            cells: columns.map((col) => ({
                columnId: `cumulated-${col.id}`,
                value: col.id === "title" ? t("Analytics.ConsolidatedSales.Total") : rowOfTotals?.cumulated?.[col.id],
            })),
            variant: "highlighted",
            placement: "footer",
        });

        return {
            inRangeRows,
            cumulatedRows,
        };
    };

    const startDateLabel = filters?.dates?.startDate
        ? formatLocaleDateString({ date: filters.dates.startDate, options: { timeZone: "UTC" } })
        : null;
    const endDateLabel = filters?.dates?.endDate
        ? formatLocaleDateString({ date: filters.dates.endDate, options: { timeZone: "UTC" } })
        : null;

    const rangeLabel = startDateLabel && endDateLabel ? `${startDateLabel} - ${endDateLabel}` : "";
    const cumulatedLabel = t("Analytics.ConsolidatedSales.CumulatedTableTitle", {
        date: endDateLabel,
    });

    useEffect(() => {
        if (!isRefreshing && companyId && visibleTableIds.current?.length) {
            const channelsToFetch = visibleTableIds.current.filter((channelId) => {
                // Without its total, as before: every visible table not folded by the user
                return hasNoTotal(channelId) ? !collapsedState[channelId] : collapsedState[channelId] === false;
            });

            fetchNextPageRef.current?.({
                channelsToFetch: channelsToFetch ? channelsToFetch : [],
            });
        }
    }, [
        visibleTableIdsKey,
        collapsedState,
        companyId,
        filters?.dates,
        sort,
        channelIds,
        tablesData,
        isRefreshing,
        totalsUnavailable,
        totalsStatus,
    ]);

    useEffect(() => {
        const containerElem = containerRef.current;

        if (containerElem) {
            const onScroll = (e?: Event) => {
                const scrollPosition = e ? (e.currentTarget as Window).scrollY : 0;

                const visibleTables = tableYPos.filter(
                    ({ y, b }) =>
                        (y >= scrollPosition && y <= scrollPosition + windowHeight) ||
                        (b >= scrollPosition && b <= scrollPosition + windowHeight),
                );

                visibleTableIds.current = visibleTables.map((o) => o.id).reverse();
                setVisibleTableIdsKey(visibleTableIds.current.join("-"));
            };

            onScroll();

            window.addEventListener("scroll", onScroll);

            return () => {
                window.removeEventListener("scroll", onScroll);
            };
        }
    }, [tableYPos, windowHeight]);

    const handleCalculateTablePositions = useRef(() => {
        Object.keys(tableRefs.current).forEach((groupId) => {
            const tableElem = tableRefs.current[groupId];

            const rect = tableElem.getBoundingClientRect();
            if (rect) {
                setTableYPos((currentState) => {
                    const newState = currentState.filter((o) => o.id !== groupId);
                    newState.push({ id: groupId, y: rect.top, b: rect.bottom });
                    return newState.sort((a, b) => b.y - a.y);
                });
            }
        });
    });

    useEffect(() => {
        handleCalculateTablePositions.current();
    }, [tablesData, channelIds, filters]);

    useEffect(() => {
        const onResize = () => {
            handleCalculateTablePositions.current();
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener("resize", onResize);

        onResize();

        return () => {
            window.removeEventListener("resize", onResize);
        };
    }, []);

    return (
        <div className="relative flex flex-col py-[1rem]!">
            <div className="flex-none py-[1rem]!">
                <Filters
                    isLoading={false}
                    periodsDateRange={periodsDateRange}
                    titlesPaginationData={titlesPaginationData}
                    onOptionSelect={handleOptionSelect}
                    filters={filters}
                    storedPreferences={storedPreferences}
                />
            </div>
            <div className="relative flex-1 min-h-0 flex flex-col" ref={containerRef}>
                {channelIds?.map?.((channelId) => {
                    const { inRangeRows, cumulatedRows } = getRowsOfChannel(`${channelId}`, columns);

                    const channelName =
                        channelId === "all"
                            ? t("Analytics.ConsolidatedSales.AllChannelsTableTitle")
                            : channels.find((o) => o.id === channelId)?.name || `${channelId}`;

                    return (
                        <div
                            key={channelId}
                            ref={(el) => {
                                if (el) tableRefs.current[channelId] = el;
                            }}
                            className={"py-4! first-of-type:pt-0!"}
                        >
                            <Tables
                                key={channelId}
                                label={channelName}
                                onReflow={handleCalculateTablePositions.current}
                                isCollapsed={
                                    collapsedState[`${channelId}`] === undefined || !!collapsedState[`${channelId}`]
                                }
                                setIsCollapsed={(isCollapsed: boolean) => {
                                    setCollapsedState((o) => ({
                                        ...o,
                                        [`${channelId}`]: isCollapsed,
                                    }));
                                }}
                                isPending={
                                    isRefreshing || !tablesData?.[`${channelId}`] || !tablesData[`${channelId}`].isDone
                                }
                                sort={sort}
                                onColumnClick={handleColumnClick}
                                tables={[
                                    {
                                        columns,
                                        id: `${channelId}`,
                                        rows: inRangeRows,
                                        title: rangeLabel,
                                    },
                                    {
                                        columns: columns.map((c, i) => ({
                                            ...c,
                                            id: `cumulated-${c.id}`,
                                            isHidden: i === 0 || c.isHidden,
                                        })),
                                        id: `cumulated-${channelId}`,
                                        rows: cumulatedRows,
                                        title: cumulatedLabel,
                                    },
                                ]}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default React.memo(ConsolidatedSalesContent);
